/**
 * 监控 SSE 消息体解析（与后端 { code, data, messageBody } 结构一致）
 *
 * 后端约定：`monitoring_alarm` 每条事件只描述「一个设备 + 一个通道」的一条告警；
 * 不会在单条 payload 里混多个设备。页面多路展示来自多条事件的累积 + HTTP 初始分组。
 */

/**
 * 将 event.data 规范成单条业务对象（仍对应「单设备单通道」）。
 * 兼容传输层写法：整段或 `data` 为单元素数组、`{ code, data: {...} }` 等；
 * 若不解包，getSeeData 可能拿到 Array，getDevicePart 会得到 _unknown / _。
 * 信封可多层；外层若只有空/残缺 messageBO，继续剥内层，避免有婴儿包读不到 hasBaby。
 */
function unwrapMonitoringSeePayload(raw) {
  if (raw == null || typeof raw !== 'object') return {}
  let cur = raw
  for (let depth = 0; depth < 4; depth += 1) {
    if (Array.isArray(cur)) {
      const head = cur.length > 0 ? cur[0] : null
      if (head != null && typeof head === 'object' && !Array.isArray(head)) {
        cur = head
        continue
      }
      return {}
    }

    const latest = cur.latestMonitoringMessage
    if (
      latest && typeof latest === 'object' && !Array.isArray(latest)
      && cur.hasBaby === undefined && cur.isViolation === undefined
    ) {
      const record = {
        ...latest,
        deviceId: latest.deviceId ?? cur.deviceId,
        deviceSerial: latest.deviceSerial ?? cur.deviceSerial,
        channelNo: latest.channelNo ?? cur.channelNo ?? latest.channel_no ?? cur.channel_no,
        alarmTime: cur.alarmTime
      }
      if (cur.messageTime != null && cur.messageTime !== '') {
        record.messageTime = cur.messageTime
      }
      return record
    }

    const bo = cur.messageBO
    const boObj = (bo && typeof bo === 'object' && !Array.isArray(bo)) ? bo : null
    const hasSceneBool = cur.hasBaby !== undefined || cur.isViolation !== undefined
      || (boObj != null && (boObj.hasBaby !== undefined || boObj.isViolation !== undefined))
    const hasDeviceKey = cur.deviceId != null || cur.deviceSerial != null
    const hasBody = Boolean(boObj || cur.messageBody || cur.messageMap)

    // 业务体已带 scene 布尔，或没有更深 data 可剥 → 停
    const inner = cur.data
    const canPeel = inner != null && typeof inner === 'object'
    if (hasSceneBool || (!canPeel && (hasBody || hasDeviceKey))) {
      return cur
    }

    if (!canPeel) return cur

    if (Array.isArray(inner)) {
      const h = inner.length > 0 ? inner[0] : null
      if (h != null && typeof h === 'object' && !Array.isArray(h)) {
        cur = h
        continue
      }
      return cur
    }

    // 外层有 deviceId/空 messageBO，内层才有真 scene：合并外层设备键 + 内层业务
    if (hasDeviceKey || hasBody) {
      const innerBo = inner.messageBO
      const innerHasScene = inner.hasBaby !== undefined || inner.isViolation !== undefined
        || (innerBo && typeof innerBo === 'object' && !Array.isArray(innerBo)
          && (innerBo.hasBaby !== undefined || innerBo.isViolation !== undefined))
      if (innerHasScene || inner.messageBody || inner.messageMap) {
        return {
          ...inner,
          deviceId: cur.deviceId ?? inner.deviceId,
          deviceSerial: cur.deviceSerial ?? inner.deviceSerial,
          channelNo: cur.channelNo ?? inner.channelNo ?? cur.channel_no ?? inner.channel_no
        }
      }
    }

    cur = inner
  }
  return (cur && typeof cur === 'object' && !Array.isArray(cur)) ? cur : {}
}

/**
 * SSE 入库项为 { data: 接口体 }；接口体为 { code, data: { ... } } 或扁平一层。
 * 业务语义：一条 item 对应单设备单通道（见文件头约定）。
 */
export function getSeeData(item) {
  return unwrapMonitoringSeePayload(item?.data)
}

/**
 * 业务体：监控链路以 messageBO 为准（SSE/HTTP 已保证）；
 * 再兼容老 messageBody / messageMap。不可先取 messageBody——空对象会盖住 messageBO。
 */
export function getMessageBody(seeData) {
  if (!seeData || typeof seeData !== 'object') return null
  const bo = seeData.messageBO
  if (bo && typeof bo === 'object' && !Array.isArray(bo)) return bo
  const b = seeData.messageBody
  if (b && typeof b === 'object' && !Array.isArray(b)) return b
  const m = seeData.messageMap
  if (m && typeof m === 'object' && !Array.isArray(m)) return m
  return null
}

/** 兼容 boolean / 0|1 / "true"|"false" */
function coerceMonitoringSceneBool (raw) {
  if (typeof raw === 'boolean') return raw
  if (raw === 1 || raw === 0) return raw === 1
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase()
    if (s === 'true' || s === '1') return true
    if (s === 'false' || s === '0') return false
  }
  return undefined
}

/**
 * yolo / 萤石 scene：hasBaby、isViolation
 * messageBO 是旧包；现行 SSE / list 记录把 hasBaby、isViolation 放在记录本身。
 * 缺省再读顶层；不扫 messageBody/messageMap，避免非 scene 结构误判。
 */
export function getMonitoringSceneFlags (seeData) {
  if (!seeData || typeof seeData !== 'object') {
    return { hasBaby: undefined, isViolation: undefined }
  }
  const bo = seeData.messageBO
  const fromBo = (bo && typeof bo === 'object' && !Array.isArray(bo)) ? bo : null
  const hasBaby = coerceMonitoringSceneBool(fromBo?.hasBaby)
  const isViolation = coerceMonitoringSceneBool(fromBo?.isViolation)
  return {
    hasBaby: hasBaby !== undefined ? hasBaby : coerceMonitoringSceneBool(seeData.hasBaby),
    isViolation: isViolation !== undefined ? isViolation : coerceMonitoringSceneBool(seeData.isViolation)
  }
}

/**
 * 这条推送是否算「预警」：hasBaby===false 或 isViolation===true。
 * 监控墙格子态、播报、预警面板/铃铛计数都走这里，安全包（有婴且无异常）一律不算。
 */
export function isMonitoringAlertPayload (seeData) {
  const { hasBaby, isViolation } = getMonitoringSceneFlags(seeData)
  return hasBaby === false || isViolation === true
}

function pickMonitoringAlarmNumericId (raw) {
  if (raw == null || raw === '') return undefined
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  const s = String(raw).trim()
  if (/^\d+$/.test(s)) return Number(s)
  const wrapped = /^java_mw_(\d+)$/.exec(s)
  if (wrapped) return Number(wrapped[1])
  return undefined
}

/**
 * set_remark 提交用告警主键：兼容 seeData / messageBody / 虚拟 SSE 项 wrapper id
 */
export function getMonitoringAlarmRecordId (seeData, sourceItem) {
  const fromSee =
    pickMonitoringAlarmNumericId(seeData?.id) ??
    pickMonitoringAlarmNumericId(seeData?.alarmId)
  if (fromSee != null) return fromSee
  const body = getMessageBody(seeData)
  const fromBody =
    pickMonitoringAlarmNumericId(body?.id) ??
    pickMonitoringAlarmNumericId(body?.alarmId)
  if (fromBody != null) return fromBody
  return pickMonitoringAlarmNumericId(sourceItem?.id)
}

function createdAtToMs(createdAt) {
  if (createdAt == null || createdAt === '') return 0
  if (typeof createdAt === 'number' && !Number.isNaN(createdAt)) {
    return createdAt < 1e12 ? createdAt * 1000 : createdAt
  }
  const t = Date.parse(String(createdAt))
  return Number.isNaN(t) ? 0 : t
}

/** 秒/毫秒时间戳统一为毫秒（与 monitoringRecordTimeMs 口径一致） */
function normalizeEpochMs(raw) {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return 0
  return n < 1e12 ? n * 1000 : n
}

/** 从单层 body 取事件时间（executeTime / timePoints） */
function eventTimeMsFromBodyLayer (body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return 0
  const ex = normalizeEpochMs(body.executeTime)
  if (ex > 0) return ex
  const tp = body.timePoints
  if (Array.isArray(tp) && tp.length) {
    const n = normalizeEpochMs(String(tp[tp.length - 1]))
    if (n > 0) return n
  }
  return 0
}

export function getEventTimeMs(seeData) {
  if (!seeData) return 0
  const tAlarm = normalizeEpochMs(seeData.alarmTime)
  if (tAlarm > 0) return tAlarm
  const t0 = normalizeEpochMs(seeData.messageTime)
  if (t0 > 0) return t0
  const c = createdAtToMs(seeData.createdAt)
  if (c > 0) return c
  // messageBO 优先时可能只有 scene 无 timePoints；需继续扫 messageBody / messageMap
  const layers = [seeData.messageBO, seeData.messageBody, seeData.messageMap]
  for (let i = 0; i < layers.length; i += 1) {
    const t = eventTimeMsFromBodyLayer(layers[i])
    if (t > 0) return t
  }
  return 0
}

/**
 * 同 streamKey 多条告警中取「最新」展示项：按事件时间降序；
 * 时间相同则 SSE 增量优先于 HTTP 基线虚拟项；
 * SSE 无时间戳（0）时仍优先于 HTTP 基线（铺底项不应挡住新推送）。
 * 监控墙不用此函数（每路只留最新一条，见 sseSlice 按摄像头覆盖）；仅预警面板与 HTTP 基线合并时使用。
 */
export function pickLatestMonitoringItem(items) {
  if (!Array.isArray(items) || items.length === 0) return null
  let best = items[0]
  let bestT = getEventTimeMs(getSeeData(best))
  for (let i = 1; i < items.length; i += 1) {
    const cur = items[i]
    const t = getEventTimeMs(getSeeData(cur))
    const curJava = Boolean(cur._javaMonitoringAlarm)
    const bestJava = Boolean(best._javaMonitoringAlarm)
    if (t > bestT) {
      best = cur
      bestT = t
      continue
    }
    if (t === bestT) {
      if (bestJava && !curJava) best = cur
      continue
    }
    if (t === 0 && bestT > 0 && !curJava && bestJava) {
      best = cur
    }
  }
  return best
}

/** 设备列表 serial 与 group_unprocessed / SSE deviceId 对齐（大小写不敏感） */
export function normalizeMonitoringDeviceId (raw) {
  if (raw == null || raw === '') return '_unknown'
  const s = String(raw).trim()
  return s !== '' ? s.toUpperCase() : '_unknown'
}

/** 通道号字符串键（与 normalizeChannelNo 输出一致） */
export function normalizeMonitoringChannelKey (raw) {
  if (raw == null || raw === '') return '_'
  const s = String(raw).trim()
  return s !== '' ? s : '_'
}

/**
 * 设备卡片 / 监控墙格：由 Java 设备行的 deviceId + channelNo 生成与 getStreamKey 一致的聚合键
 */
export function buildMonitoringStreamKeyFromDevice (deviceId, channelNo) {
  const d = normalizeMonitoringDeviceId(deviceId)
  const c = normalizeMonitoringChannelKey(channelNo)
  return `${d}\u0001${c}`
}

export function getDevicePart(seeData) {
  const body = getMessageBody(seeData)
  const key =
    seeData.deviceId ||
    seeData.deviceSerial ||
    body?.deviceId ||
    body?.deviceSerial
  if (key != null && String(key).trim() !== '') return normalizeMonitoringDeviceId(key)
  return '_unknown'
}

export function normalizeChannelNo(seeData) {
  const body = getMessageBody(seeData)
  const v =
    seeData.channelNo ??
    seeData.channel_no ??
    seeData.localIndex ??
    body?.channelNo ??
    body?.channel_no ??
    body?.localIndex
  if (v != null && String(v).trim() !== '') return String(v).trim()
  return '_'
}

export function getStreamKey(item) {
  const seeData = getSeeData(item)
  return `${getDevicePart(seeData)}\u0001${normalizeChannelNo(seeData)}`
}

/**
 * 将 getStreamKey 生成的 key 拆回 deviceId + channelNo（供 HTTP 查询参数等）
 * @param {string} streamKey
 * @returns {{ deviceId: string, channelNo: string }}
 */
export function parseMonitoringStreamKey(streamKey) {
  if (streamKey == null || typeof streamKey !== 'string') {
    return { deviceId: '', channelNo: '' }
  }
  const sep = '\u0001'
  const i = streamKey.indexOf(sep)
  if (i === -1) {
    return { deviceId: streamKey.trim(), channelNo: '' }
  }
  return {
    deviceId: streamKey.slice(0, i).trim(),
    channelNo: streamKey.slice(i + 1).trim()
  }
}

/**
 * SSE `monitoring_alarm_processed` 扁平体（或一层 `data` 包裹）→ 与 getStreamKey 一致的聚合键。
 * 例：`{"channelNo":"20","deviceId":"E39129826"}`
 */
export function getStreamKeyFromMonitoringProcessedPayload(raw) {
  if (raw == null || typeof raw !== 'object') return null
  let p = raw
  if (p.data != null && typeof p.data === 'object' && !Array.isArray(p.data)) {
    p = p.data
  }
  const seeData = p
  const part = getDevicePart(seeData)
  if (part === '_unknown') return null
  return getStreamKey({ data: seeData })
}

export function getYsLiveParamsFromGroup(group) {
  if (!group || group.devicePart === '_unknown') return null
  const deviceSerial = group.devicePart
  const ch = group.channelNo
  const channelNo = ch === '_' ? 1 : parseInt(ch, 10) || 1
  return { deviceSerial, channelNo }
}

export function getYsLiveParamsFromItem(item) {
  const seeData = getSeeData(item)
  const devicePart = getDevicePart(seeData)
  if (devicePart === '_unknown') return null
  const ch = normalizeChannelNo(seeData)
  const channelNo = ch === '_' ? 1 : parseInt(ch, 10) || 1
  return { deviceSerial: devicePart, channelNo }
}

export function toSafeDomIdPart(s) {
  return String(s).replace(/[^a-zA-Z0-9_-]/g, '_')
}

function firstUrlInArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return ''
  const u = arr.find((x) => typeof x === 'string' && x.trim() !== '')
  return u ? u.trim() : ''
}

function filterUrlArray(arr) {
  if (!Array.isArray(arr)) return []
  return arr
    .filter((u) => typeof u === 'string' && u.trim())
    .map((u) => u.trim())
}

function monitoringImageLayers (seeData) {
  if (!seeData || typeof seeData !== 'object') return []
  return [seeData, seeData.messageBO, seeData.messageBody, seeData.messageMap]
}

/** 列表缩略图：优先 thumbnailUrls（与 NursingAlertsForm / YsDeviceDrawerBody 一致） */
export function getMonitoringThumbnailUrl(seeData) {
  const layers = monitoringImageLayers(seeData)
  for (let i = 0; i < layers.length; i += 1) {
    const body = layers[i]
    if (!body || typeof body !== 'object' || Array.isArray(body)) continue
    const thumb = firstUrlInArray(body.thumbnailUrls)
    if (thumb) return thumb
  }
  for (let i = 0; i < layers.length; i += 1) {
    const body = layers[i]
    if (!body || typeof body !== 'object' || Array.isArray(body)) continue
    const full = firstUrlInArray(body.imageUrls)
    if (full) return full
  }
  return ''
}

/** SSE/推理婴儿图：优先 imageUrls[0]，无则回退 thumbnailUrls[0]；messageBO 无图时继续看 messageBody */
export function getMonitoringFullImageUrl(seeData) {
  const layers = monitoringImageLayers(seeData)
  for (let i = 0; i < layers.length; i += 1) {
    const body = layers[i]
    if (!body || typeof body !== 'object' || Array.isArray(body)) continue
    const full = firstUrlInArray(body.imageUrls)
    if (full) return full
  }
  for (let i = 0; i < layers.length; i += 1) {
    const body = layers[i]
    if (!body || typeof body !== 'object' || Array.isArray(body)) continue
    const thumb = firstUrlInArray(body.thumbnailUrls)
    if (thumb) return thumb
  }
  return ''
}

/**
 * 截图弹窗 URL 列表：首张为与列表缩略图同帧的大图，其余为同条告警的其它大图。
 */
export function getMonitoringSnapshotPreviewUrls(seeData) {
  const layers = monitoringImageLayers(seeData)
  const thumbs = []
  const fulls = []
  for (let i = 0; i < layers.length; i += 1) {
    const body = layers[i]
    if (!body || typeof body !== 'object' || Array.isArray(body)) continue
    const t = filterUrlArray(body.thumbnailUrls)
    const f = filterUrlArray(body.imageUrls)
    for (let j = 0; j < t.length; j += 1) {
      if (!thumbs.includes(t[j])) thumbs.push(t[j])
    }
    for (let j = 0; j < f.length; j += 1) {
      if (!fulls.includes(f[j])) fulls.push(f[j])
    }
  }
  const primary = fulls[0] || thumbs[0]
  if (!primary) return []
  const out = [primary]
  for (let i = 1; i < fulls.length; i += 1) {
    if (!out.includes(fulls[i])) out.push(fulls[i])
  }
  if (fulls.length === 0) {
    for (let i = 1; i < thumbs.length; i += 1) {
      if (!out.includes(thumbs[i])) out.push(thumbs[i])
    }
  }
  return out
}

export function normalizeImageUrls(seeData) {
  const body = getMessageBody(seeData)
  if (!body || typeof body !== 'object') return []
  let urls = body.imageUrls
  if (!Array.isArray(urls) || urls.length === 0) {
    urls = body.thumbnailUrls
  }
  if (!Array.isArray(urls)) return []
  return urls.filter((u) => typeof u === 'string' && u.trim())
}

/** 事件/协议类型：如 ys.open.ai.agent */
export function getMonitoringEventTypeCode(seeData) {
  if (!seeData) return ''
  return String(seeData.type || seeData.messageType || seeData.subType || seeData.subTypeDesc || '').trim()
}

/**
 * 长文案说明（与「类型 code」分离）：messageBO.summary 或 messageBody.result.summary
 */
export function getMonitoringAlertSummary(seeData) {
  if (seeData && typeof seeData.summary === 'string' && seeData.summary.trim()) {
    return seeData.summary.trim()
  }
  const body = getMessageBody(seeData)
  if (body && typeof body.summary === 'string' && body.summary.trim()) {
    return body.summary.trim()
  }
  const r = body && body.result && body.result.summary
  if (typeof r === 'string' && r.trim()) return r.trim()
  return ''
}

/**
 * 严重等级：用于列表行高亮（如 level.desc 含「严重」、或 CRITICAL、或 value=4 特急档）
 */
export function isSevereMonitoringLevel(seeData) {
  const lv = seeData?.level
  if (!lv || typeof lv !== 'object') return false
  const d = String(lv.desc != null ? lv.desc : '')
  if (d.includes('严重')) return true
  if (String(lv.name || '').toUpperCase() === 'CRITICAL') return true
  if (lv.value === 4) return true
  return false
}

export function getEventTypeLabel(seeData) {
  if (seeData && typeof seeData.summary === 'string' && seeData.summary.trim()) {
    return seeData.summary.trim()
  }
  const body = getMessageBody(seeData)
  const r = body && body.result && body.result.summary
  if (typeof r === 'string' && r.trim()) return r.trim()
  if (body && typeof body.summary === 'string' && body.summary.trim()) return body.summary.trim()
  return getMonitoringEventTypeCode(seeData) || seeData.subTypeDesc || ''
}

export function sortSseItemsByTimeDesc(items) {
  if (!Array.isArray(items)) return []
  return [...items].sort(
    (a, b) => getEventTimeMs(getSeeData(b)) - getEventTimeMs(getSeeData(a))
  )
}

/**
 * 监控链路是否已处理：以接口/SSE 上的 isProcessed（boolean）为准；无则 undefined，由展示层回退 status 文案
 */
export function getMonitoringIsProcessed(item) {
  if (!item || typeof item !== 'object') return undefined
  if (typeof item.isProcessed === 'boolean') return item.isProcessed
  const see = getSeeData(item)
  if (see && typeof see.isProcessed === 'boolean') return see.isProcessed
  return undefined
}

/** Java 接口 createdAt：支持秒/毫秒时间戳或 ISO 字符串 */
export function parseJavaAlarmCreatedAtMs(createdAt) {
  if (createdAt == null || createdAt === '') return Date.now()
  if (typeof createdAt === 'number' && !Number.isNaN(createdAt)) {
    return createdAt < 1e12 ? createdAt * 1000 : createdAt
  }
  const t = Date.parse(String(createdAt))
  return Number.isNaN(t) ? Date.now() : t
}

/**
 * 将 Java 分组里的一条 alarm 转为与 SSE 条一致的 { data } 结构（供 getSeeData / 列表 / 抽屉表格使用）
 * 新接口见数据多为扁平：deviceId, channelNo, type, level, messageBO{ imageUrls, summary, timePoints, ... }。
 */
export function buildVirtualSseItemFromJavaAlarm(groupDeviceId, groupChannelNo, alarm) {
  if (!alarm || typeof alarm !== 'object') return null
  const deviceId = String(alarm.deviceId ?? groupDeviceId ?? '').trim() || '_unknown'
  const rawCh =
    alarm.channelNo != null && String(alarm.channelNo).trim() !== ''
      ? alarm.channelNo
      : groupChannelNo
  const channelNo =
    rawCh != null && String(rawCh).trim() !== '' ? String(rawCh).trim() : '_'
  const typeLabel = String(alarm.type ?? '').trim()

  const createdMs = parseJavaAlarmCreatedAtMs(alarm.createdAt)
  let messageTime = createdMs
  const alarmBody = alarm.messageBO || alarm.messageMap
  const tp0 = alarmBody && Array.isArray(alarmBody.timePoints) ? alarmBody.timePoints : null
  if (tp0 && tp0.length) {
    const n = Number(String(tp0[tp0.length - 1]))
    if (n > 0) messageTime = n < 1e12 ? n * 1000 : n
  }

  const status = alarm.isProcessed ? '已结束' : '未处理'
  const chForSee =
    channelNo === '_' ? 1 : Number.isNaN(Number(channelNo)) ? 1 : Number(channelNo)

  const seeInner = {
    ...alarm,
    deviceId,
    channelNo: chForSee,
    messageType: typeLabel || 'java.monitoring',
    subType: typeLabel,
    subTypeDesc: typeLabel,
    messageTime
  }

  return {
    id: `java_mw_${alarm.id != null ? alarm.id : `${deviceId}_${channelNo}_${messageTime}`}`,
    status,
    isProcessed: typeof alarm.isProcessed === 'boolean' ? alarm.isProcessed : undefined,
    _javaMonitoringAlarm: true,
    data: { data: seeInner }
  }
}

/**
 * 从分组对象取子告警列表（兼容后端字段名 alarmList / alarms / list）
 */
export function getAlarmListFromMonitoringGroup(g) {
  if (!g || typeof g !== 'object') return []
  if (Array.isArray(g.alarmList)) return g.alarmList
  if (Array.isArray(g.alarms)) return g.alarms
  if (Array.isArray(g.list)) return g.list
  return []
}

/** 有选中门店时：分组有 storeId 则整组对齐；否则只留该店告警。无门店字段的分组丢掉。 */
export function filterMonitoringGroupsByStoreId(groups, selectedStoreId) {
  if (!selectedStoreId) return Array.isArray(groups) ? groups : []
  if (!Array.isArray(groups)) return []
  const out = []
  for (let i = 0; i < groups.length; i += 1) {
    const group = groups[i]
    if (!group || typeof group !== 'object') continue
    const groupStoreId = String(group.storeId ?? group.store_id ?? '').trim()
    if (groupStoreId) {
      if (groupStoreId === selectedStoreId) out.push(group)
      continue
    }
    const alarms = getAlarmListFromMonitoringGroup(group)
    if (alarms.length === 0) continue
    const filteredAlarms = []
    for (let j = 0; j < alarms.length; j += 1) {
      const alarm = alarms[j]
      const m = alarm?.messageMap || alarm?.messageBody
      const aid = String(alarm?.storeId ?? alarm?.store_id ?? m?.storeId ?? m?.store_id ?? '').trim()
      if (aid === selectedStoreId) filteredAlarms.push(alarm)
    }
    if (filteredAlarms.length === 0) continue
    out.push({ ...group, alarmList: filteredAlarms })
  }
  return out
}

/**
 * 将「Api.get 已解包后的返回值」或 axios 体解析为 group 数组
 * （因 Java 的 code 与 data 结构差异，在 thunk 中统一用此函数，避免 groups 落库为空）
 */
export function extractMonitoringGroupListFromResponse(res) {
  const d = res?.data
  if (d == null) return []
  if (Array.isArray(d)) return d
  if (Array.isArray(d.data)) return d.data
  if (Array.isArray(d.list)) return d.list
  if (Array.isArray(d.records)) return d.records
  if (d.data != null && typeof d.data === 'object' && Array.isArray(d.data.list)) {
    return d.data.list
  }
  return []
}

/**
 * GET /alarm/monitoring/group_unprocessed 的 data 数组 → 扁平化的「类 SSE」项，供与 SSE 合并展示
 */
export function groupsToVirtualSseItems(groups) {
  if (!Array.isArray(groups) || groups.length === 0) return []
  const out = []
  for (let i = 0; i < groups.length; i += 1) {
    const g = groups[i] || {}
    const list = getAlarmListFromMonitoringGroup(g)
    if (list.length === 0) continue
    for (let j = 0; j < list.length; j += 1) {
      const item = buildVirtualSseItemFromJavaAlarm(g.deviceId, g.channelNo, list[j])
      if (item) out.push(item)
    }
  }
  return out
}
